import React, { useRef, useState } from "react";
import { TextInput, Textarea, Text, Flex } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "#utils/spacing";
import { SubtaskItemType } from "../types";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import type { SubtaskItem } from "../types";

interface InputFieldProps {
  item: SubtaskItem;
  disabled?: boolean;
}

const getStringConfig = (config: Record<string, unknown>, key: string): string | undefined => {
  const val = config[key];
  return typeof val === "string" ? val : undefined;
};

const INPUT_TYPE_BY_ITEM_TYPE: Partial<Record<SubtaskItemType, "number" | "currency">> = {
  [SubtaskItemType.NUMBER]: "number",
  [SubtaskItemType.CURRENCY]: "currency",
};

export const InputField: React.FC<InputFieldProps> = ({ item, disabled = false }) => {
  const initial = typeof item.value === "string" ? item.value : "";
  const [value, setValue] = useState<string>(initial);
  const lastSavedRef = useRef<string>(initial);
  const { updateSubtaskValue } = useSubtaskActionsContext();

  const config = item.config || {};
  const rawInputType =
    getStringConfig(config, "input_type") ??
    getStringConfig(config, "inputType") ??
    INPUT_TYPE_BY_ITEM_TYPE[item.itemType] ??
    "text";
  const isNumeric =
    rawInputType === "number" || rawInputType === "currency" || rawInputType === "percent";
  const placeholder = getStringConfig(config, "placeholder") ?? "";

  const currencyCode =
    item.itemType === SubtaskItemType.CURRENCY
      ? getStringConfig(config, "currency")
      : undefined;
  const suffix = getStringConfig(config, "suffix") ?? currencyCode;
  const min = typeof config.min === "number" ? config.min : undefined;
  const max = typeof config.max === "number" ? config.max : undefined;
  const isTextarea = item.itemType === SubtaskItemType.TEXTAREA;
  const displayLabel = item.isRequired ? `${item.label} *` : item.label;

  const isUrlReadOnly = disabled && rawInputType === "url" && value.trim() !== "";

  const handleSetValue = (next: string) => {
    if (!isNumeric) {
      setValue(next);
      return;
    }
    const sign = next.startsWith("-") ? "-" : "";
    const digitsAndDot = next.replace(/[^0-9.]/g, "");
    const [whole, ...rest] = digitsAndDot.split(".");
    const cleaned = rest.length === 0 ? whole : `${whole}.${rest.join("")}`;
    setValue(`${sign}${cleaned}`);
  };

  const handleBlur = () => {
    if (disabled) return;
    if (value === lastSavedRef.current) return;
    lastSavedRef.current = value;
    void updateSubtaskValue(item.id, value, !!item.isCompleted);
  };

  if (isUrlReadOnly) {
    return (
      <Flex
        flexDirection="column"
        gap={SPACING.X_SMALL}
        className={styles.urlReadOnlyGroup}
        data-testid={`url-readonly-${item.id}`}
      >
        {item.label && <span className={styles.urlReadOnlyLabel}>{item.label}</span>}
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.urlReadOnlyLink}
          data-testid={`url-readonly-link-${item.id}`}
        >
          {value}
        </a>
        {item.helpText && (
          <Text variant="secondary-sm" color={Colors.Neutral500} className={styles.helpText}>
            {item.helpText}
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <div className={styles.inputWrapper} onBlur={handleBlur}>
      {isTextarea ? (
        <Textarea
          name={`input-${item.id}`}
          label={displayLabel}
          value={value}
          onSetValue={setValue}
          placeholder={placeholder}
          disabled={disabled}
          size="medium"
          isFullWidth
        />
      ) : (
        <TextInput
          name={`input-${item.id}`}
          label={displayLabel}
          value={value}
          onSetValue={handleSetValue}
          placeholder={placeholder}
          disabled={disabled}
          type={isNumeric ? "number" : "text"}
          min={min}
          max={max}
          isFullWidth
        />
      )}
      {suffix && (
        <Text variant="secondary-sm" color={Colors.Neutral500} className={styles.inputSuffix}>
          {suffix}
        </Text>
      )}
      {item.helpText && (
        <Text variant="secondary-sm" color={Colors.Neutral500} className={styles.helpText}>
          {item.helpText}
        </Text>
      )}
    </div>
  );
};

export default InputField;
